import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaEraser } from "react-icons/fa";
import {
  MdBrush,
  MdDeleteOutline,
  MdFitScreen,
  MdPanTool,
  MdTitle,
  MdZoomIn,
  MdZoomOut,
} from "react-icons/md";
import { PDFDocument } from "pdf-lib";
import * as pdfjs from "pdfjs-dist";
import { toast } from "react-toastify";
import { brandPalette } from "../../../constants/brandPalette";
import { getStudentHomeworkFile } from "../../../modules/admin-modules/reports/Api";
import ExamPageFeedback from "../correct-student-exams/exam-page-feedback";
import ExamResultsCover from "../correct-student-exams/exam-results-cover";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const DESKTOP_RENDER_SCALE = 1.5;
const IOS_RENDER_SCALE = 1;
const ZOOM_STEP = 0.25;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3;
const IOS_MAX_ZOOM = 2;
const IOS_MAX_CANVAS_EDGE = 1536;
const DESKTOP_MAX_CANVAS_EDGE = 2560;
const DEFAULT_STROKE_COLOR = "#E53E3E";
const DEFAULT_STROKE_WIDTH = 2.5;

function isAppleTouchDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return (
    /iP(hone|ad|od)/.test(ua) ||
    (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1)
  );
}

function getPreferredRenderScale() {
  return isAppleTouchDevice() ? IOS_RENDER_SCALE : DESKTOP_RENDER_SCALE;
}

function getMaxCanvasEdge() {
  return isAppleTouchDevice() ? IOS_MAX_CANVAS_EDGE : DESKTOP_MAX_CANVAS_EDGE;
}

function getMaxZoom() {
  return isAppleTouchDevice() ? IOS_MAX_ZOOM : MAX_ZOOM;
}

function getNearbyPageRange() {
  return isAppleTouchDevice() ? 1 : 2;
}

function getCappedViewport(page, preferredScale, maxEdge) {
  const unit = page.getViewport({ scale: 1 });
  const longest = Math.max(unit.width, unit.height) || 1;
  const scale = Math.min(preferredScale, maxEdge / longest);
  return page.getViewport({ scale: Math.max(0.25, scale) });
}

function releaseCanvas(canvas) {
  if (!canvas) return;
  try {
    canvas.width = 0;
    canvas.height = 0;
  } catch {
    // Ignore if the canvas is already gone.
  }
}

async function destroyPdfDoc(pdfDoc) {
  if (!pdfDoc) return;
  try {
    await pdfDoc.destroy();
  } catch {
    try {
      pdfDoc.cleanup?.();
    } catch {
      // Ignore shutdown errors.
    }
  }
}

const ANNOTATION_COLORS = [
  { id: "white", label: "White", value: "#FFFFFF" },
  { id: "red", label: "Red", value: "#E53E3E" },
  { id: "blue", label: "Blue", value: "#3182CE" },
  { id: "green", label: "Green", value: "#38A169" },
];

const STROKE_SIZES = [
  { id: "s", label: "S", width: 2.5, fontSize: 16 },
  { id: "m", label: "M", width: 5, fontSize: 22 },
  { id: "l", label: "L", width: 9, fontSize: 32 },
  { id: "xl", label: "XL", width: 16, fontSize: 48 },
];

function fontSizeFromStroke(strokeWidth) {
  const match = STROKE_SIZES.find((size) => size.width === strokeWidth);
  return match?.fontSize || 16;
}

function paintTextOnCanvas(canvas, { x, y, text, fontSize, color }) {
  if (!canvas) return;
  const lines = String(text || "")
    .split("\n")
    .map((line) => line.trimEnd())
    .filter((line, index, all) => line.length > 0 || index < all.length - 1);
  if (!lines.length) return;

  const ctx = canvas.getContext("2d");
  ctx.save();
  ctx.globalCompositeOperation = "source-over";
  ctx.fillStyle = color;
  ctx.font = `700 ${fontSize}px Arial, sans-serif`;
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  if (color === "#FFFFFF") {
    ctx.shadowColor = "rgba(0, 0, 0, 0.45)";
    ctx.shadowBlur = 1.5;
  } else {
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
  }
  const lineHeight = fontSize * 1.25;
  lines.forEach((line, index) => {
    ctx.fillText(line, x, y + index * lineHeight);
  });
  ctx.restore();
}

// iOS Safari treats <canvas> like an image: long-press / double-tap shows
// Copy | Search with Google and selects the whole page. Keep drawing on a
// non-image overlay and disable native callout/selection on the canvases.
const IOS_NO_SELECT_STYLE = {
  WebkitUserSelect: "none",
  userSelect: "none",
  WebkitTouchCallout: "none",
  WebkitUserDrag: "none",
  WebkitTapHighlightColor: "transparent",
};

function prefersFingerPan() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(pointer: coarse)").matches;
}

function touchDistance(a, b) {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

function touchCenter(a, b) {
  return {
    x: (a.clientX + b.clientX) / 2,
    y: (a.clientY + b.clientY) / 2,
  };
}

function getCanvasPoint(canvas, clientX, clientY) {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  return {
    x: (clientX - rect.left) * scaleX,
    y: (clientY - rect.top) * scaleY,
  };
}

function canvasToPngBytes(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      async (blob) => {
        if (!blob) {
          reject(new Error("Could not export annotation layer."));
          return;
        }
        resolve(await blob.arrayBuffer());
      },
      "image/png"
    );
  });
}

async function fetchHomeworkPdfBytes(filename) {
  const response = await getStudentHomeworkFile(filename);
  return response.data;
}

async function defaultFetchPdfBytes(filename) {
  return fetchHomeworkPdfBytes(filename);
}

async function buildAnnotatedPdf(originalBytes, annotationCanvases) {
  const pdfDoc = await PDFDocument.load(originalBytes);
  const pages = pdfDoc.getPages();

  for (let i = 0; i < pages.length; i++) {
    // UI stores annotations with 1-based page numbers; pdf-lib pages are 0-based.
    const canvas = annotationCanvases[i + 1];
    if (!canvas || canvas.width < 1 || canvas.height < 1) continue;

    const pngBytes = await canvasToPngBytes(canvas);
    const pngImage = await pdfDoc.embedPng(pngBytes);
    const page = pages[i];
    const { width, height } = page.getSize();
    page.drawImage(pngImage, { x: 0, y: 0, width, height });
  }

  return pdfDoc.save();
}

function applyStrokeStyle(ctx, strokeColor, strokeWidth, erasing = false) {
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  if (erasing) {
    ctx.globalCompositeOperation = "destination-out";
    ctx.strokeStyle = "rgba(0, 0, 0, 1)";
    ctx.lineWidth = Math.max(strokeWidth * 1.8, strokeWidth + 4);
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    return;
  }
  ctx.globalCompositeOperation = "source-over";
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = strokeWidth;
  if (strokeColor === "#FFFFFF") {
    ctx.shadowColor = "rgba(0, 0, 0, 0.45)";
    ctx.shadowBlur = 1.5;
  } else {
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
  }
}

function TextDraftInput({
  draft,
  color,
  fontSize,
  displayZoom,
  canvasWidth,
  canvasHeight,
  onChange,
  onCommit,
  onCancel,
}) {
  const inputRef = useRef(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    const node = inputRef.current;
    if (!node) return;
    node.focus();
    node.style.height = "auto";
    node.style.height = `${node.scrollHeight}px`;
  }, [draft.sourcePage, draft.x, draft.y]);

  const commit = () => {
    if (cancelledRef.current) return;
    onCommit(inputRef.current?.value ?? draft.text);
  };

  return (
    <textarea
      ref={inputRef}
      value={draft.text}
      aria-label="PDF text annotation"
      placeholder="Type here"
      onChange={(event) => {
        onChange(event.target.value);
        event.target.style.height = "auto";
        event.target.style.height = `${event.target.scrollHeight}px`;
      }}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          cancelledRef.current = true;
          onCancel();
          return;
        }
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          commit();
        }
      }}
      rows={1}
      style={{
        position: "absolute",
        left: `${(draft.x / canvasWidth) * 100}%`,
        top: `${(draft.y / canvasHeight) * 100}%`,
        zIndex: 6,
        margin: 0,
        minWidth: "72px",
        maxWidth: `calc(100% - ${(draft.x / canvasWidth) * 100}%)`,
        fontSize: `${Math.max(12, fontSize * displayZoom)}px`,
        lineHeight: 1.25,
        fontWeight: 700,
        fontFamily: "Arial, sans-serif",
        color,
        background: color === "#FFFFFF" ? "rgba(0, 0, 0, 0.35)" : "rgba(255, 255, 255, 0.92)",
        border: `1px dashed ${color === "#FFFFFF" ? "#CBD5E0" : color}`,
        borderRadius: "4px",
        outline: "none",
        resize: "none",
        overflow: "hidden",
        padding: "1px 4px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
        WebkitUserSelect: "text",
        userSelect: "text",
        WebkitTouchCallout: "default",
      }}
    />
  );
}

const PdfPageCanvas = React.memo(function PdfPageCanvas({
  sourcePage,
  strokeColorRef,
  strokeWidthRef,
  isErasingRef,
  fingerDraw,
  isErasing,
  isTexting,
  onActivatePage,
  onPlaceText,
  onAnnotate,
  registerPdfCanvas,
  registerAnnotationCanvas,
}) {
  const drawingRef = useRef(false);
  const activePointersRef = useRef(0);
  const annotationCanvasRef = useRef(null);
  const hitLayerRef = useRef(null);

  const setPdfRef = useCallback(
    (node) => registerPdfCanvas(sourcePage, node),
    [registerPdfCanvas, sourcePage]
  );

  const setAnnotationRef = useCallback(
    (node) => {
      annotationCanvasRef.current = node;
      registerAnnotationCanvas(sourcePage, node);
    },
    [registerAnnotationCanvas, sourcePage]
  );

  const shouldDrawWithPointer = (event) => {
    if (event.pointerType === "pen" || event.pointerType === "mouse") return true;
    if (event.pointerType === "touch") return fingerDraw;
    return true;
  };

  const capturePointer = (event) => {
    const target = event.currentTarget;
    if (event.pointerId == null || !target?.setPointerCapture) return;
    try {
      target.setPointerCapture(event.pointerId);
    } catch {
      // Some browsers throw if the pointer is already gone.
    }
  };

  const releasePointer = (event) => {
    const target = event.currentTarget;
    if (event.pointerId == null || !target?.releasePointerCapture) return;
    try {
      if (target.hasPointerCapture?.(event.pointerId)) {
        target.releasePointerCapture(event.pointerId);
      }
    } catch {
      // Ignore capture cleanup errors.
    }
  };

  const startDrawing = (event) => {
    const canvas = annotationCanvasRef.current;
    if (!canvas) return;
    const point = getCanvasPoint(canvas, event.clientX, event.clientY);
    if (isTexting) {
      onActivatePage?.(sourcePage);
      onPlaceText?.(sourcePage, point);
      return;
    }
    drawingRef.current = true;
    onActivatePage?.(sourcePage);
    onAnnotate?.(sourcePage);
    capturePointer(event);
    const ctx = canvas.getContext("2d");
    applyStrokeStyle(
      ctx,
      strokeColorRef.current,
      strokeWidthRef.current,
      isErasingRef.current
    );
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const draw = (event) => {
    if (!drawingRef.current) return;
    const canvas = annotationCanvasRef.current;
    if (!canvas) return;
    const point = getCanvasPoint(canvas, event.clientX, event.clientY);
    const ctx = canvas.getContext("2d");
    applyStrokeStyle(
      ctx,
      strokeColorRef.current,
      strokeWidthRef.current,
      isErasingRef.current
    );
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    drawingRef.current = false;
  };

  useEffect(() => {
    const node = hitLayerRef.current;
    if (!node) return undefined;

    const blockNativeSelect = fingerDraw || isTexting;
    const onTouchStart = (event) => {
      window.getSelection?.()?.removeAllRanges?.();
      if (!blockNativeSelect) return;
      if (event.cancelable) event.preventDefault();
    };
    const onTouchMove = (event) => {
      if (!blockNativeSelect) return;
      if (event.cancelable) event.preventDefault();
    };
    const prevent = (event) => event.preventDefault();

    node.addEventListener("touchstart", onTouchStart, { passive: false });
    node.addEventListener("touchmove", onTouchMove, { passive: false });
    node.addEventListener("contextmenu", prevent);
    node.addEventListener("selectstart", prevent);
    node.addEventListener("dragstart", prevent);
    return () => {
      node.removeEventListener("touchstart", onTouchStart);
      node.removeEventListener("touchmove", onTouchMove);
      node.removeEventListener("contextmenu", prevent);
      node.removeEventListener("selectstart", prevent);
      node.removeEventListener("dragstart", prevent);
    };
  }, [fingerDraw, isTexting]);

  const cursor = isTexting ? "text" : !fingerDraw ? "grab" : isErasing ? "cell" : "crosshair";
  const canvasLayerStyle = {
    position: "absolute",
    left: 0,
    top: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    ...IOS_NO_SELECT_STYLE,
  };

  return (
    <>
      <canvas ref={setPdfRef} draggable={false} style={canvasLayerStyle} />
      <canvas ref={setAnnotationRef} draggable={false} style={canvasLayerStyle} />
      <div
        ref={hitLayerRef}
        role="presentation"
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: "100%",
          height: "100%",
          cursor,
          touchAction: fingerDraw ? "none" : "pan-x pan-y",
          // Opaque to Safari's image callout without changing how the PDF looks.
          backgroundImage: "linear-gradient(transparent, transparent)",
          ...IOS_NO_SELECT_STYLE,
        }}
        onContextMenu={(event) => event.preventDefault()}
        onDoubleClick={(event) => event.preventDefault()}
        onPointerDown={(event) => {
          activePointersRef.current += 1;
          if (event.pointerType === "touch" && activePointersRef.current > 1) {
            stopDrawing();
            return;
          }
          if (isTexting) {
            event.preventDefault();
            startDrawing(event);
            return;
          }
          if (!shouldDrawWithPointer(event)) return;
          event.preventDefault();
          startDrawing(event);
        }}
        onPointerMove={(event) => {
          if (!drawingRef.current) return;
          draw(event);
        }}
        onPointerUp={(event) => {
          activePointersRef.current = Math.max(0, activePointersRef.current - 1);
          stopDrawing();
          releasePointer(event);
        }}
        onPointerCancel={(event) => {
          activePointersRef.current = Math.max(0, activePointersRef.current - 1);
          stopDrawing();
          releasePointer(event);
        }}
        onLostPointerCapture={stopDrawing}
      />
    </>
  );
});

const HomeworkPdfAnnotator = ({
  isOpen,
  onClose,
  filename,
  onSave,
  fetchPdfBytes = defaultFetchPdfBytes,
  modalTitle = "Annotate homework PDF",
  defaultFileName = "homework.pdf",
  loadErrorHint = "Homework file endpoint not found. Add GET /api/admin/student_homeworks/file/{filename} on the server.",
  embedded = false,
  pageFeedback = null,
  feedbackContextLabel = "",
  coverPage = null,
  toolbarActions = null,
  saveRef = null,
}) => {
  const viewportContainerRef = useRef(null);
  const pagesScrollRef = useRef(null);
  const pdfCanvasRefs = useRef({});
  const annotationCanvasRefs = useRef({});
  const pageWrapperRefs = useRef({});
  const pdfBytesRef = useRef(null);
  const pdfDocRef = useRef(null);
  const renderScaleRef = useRef(getPreferredRenderScale());
  const maxCanvasEdgeRef = useRef(getMaxCanvasEdge());
  const annotationSnapshotsRef = useRef({});
  const dirtyPagesRef = useRef({});
  const renderTasksRef = useRef({});
  const pinchRafRef = useRef(0);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pageMetas, setPageMetas] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [autoFit, setAutoFit] = useState(true);
  const [manualZoom, setManualZoom] = useState(1);
  const [fitZoom, setFitZoom] = useState(1);
  const [strokeColor, setStrokeColor] = useState(DEFAULT_STROKE_COLOR);
  const [strokeWidth, setStrokeWidth] = useState(DEFAULT_STROKE_WIDTH);
  const [fingerDraw, setFingerDraw] = useState(() => !prefersFingerPan());
  const [isErasing, setIsErasing] = useState(false);
  const [isTexting, setIsTexting] = useState(false);
  const [textDraft, setTextDraft] = useState(null);
  const strokeColorRef = useRef(strokeColor);
  const strokeWidthRef = useRef(strokeWidth);
  const isErasingRef = useRef(isErasing);
  const displayZoomRef = useRef(1);
  const textDraftRef = useRef(null);
  strokeColorRef.current = strokeColor;
  strokeWidthRef.current = strokeWidth;
  isErasingRef.current = isErasing;
  textDraftRef.current = textDraft;

  const pageCount = pageMetas.length;
  const hasCover = Boolean(coverPage);
  const displayPages = useMemo(() => {
    if (!pageMetas.length) return [];
    if (!hasCover) {
      return pageMetas.map((page) => ({
        ...page,
        isCover: false,
        displayPage: page.pageNumber,
        sourcePage: page.pageNumber,
      }));
    }
    const first = pageMetas[0];
    return [
      {
        isCover: true,
        displayPage: 1,
        sourcePage: null,
        width: first.width,
        height: first.height,
      },
      ...pageMetas.map((page) => ({
        ...page,
        isCover: false,
        displayPage: page.pageNumber + 1,
        sourcePage: page.pageNumber,
      })),
    ];
  }, [pageMetas, hasCover]);
  const displayPageCount = displayPages.length;
  const displayZoom = autoFit ? fitZoom : manualZoom;
  displayZoomRef.current = displayZoom;
  const nearbyRange = getNearbyPageRange();
  const nearbySourcePages = useMemo(() => {
    const set = new Set();
    displayPages.forEach((page) => {
      if (!page.sourcePage) return;
      if (Math.abs(page.displayPage - currentPage) <= nearbyRange) {
        set.add(page.sourcePage);
      }
    });
    return set;
  }, [displayPages, currentPage, nearbyRange]);
  const maxPageWidth = useMemo(
    () => pageMetas.reduce((max, page) => Math.max(max, page.width), 0),
    [pageMetas]
  );

  const markAnnotated = useCallback((pageNumber) => {
    if (!pageNumber) return;
    dirtyPagesRef.current[pageNumber] = true;
  }, []);

  const snapshotAnnotation = useCallback((pageNumber, canvas) => {
    if (!pageNumber || !canvas || canvas.width < 1 || canvas.height < 1) return;
    if (!dirtyPagesRef.current[pageNumber]) return;
    try {
      annotationSnapshotsRef.current[pageNumber] = canvas
        .getContext("2d")
        .getImageData(0, 0, canvas.width, canvas.height);
    } catch {
      // iOS can throw if the backing store was already discarded.
    }
  }, []);

  const restoreAnnotation = useCallback((pageNumber, canvas) => {
    const snap = annotationSnapshotsRef.current[pageNumber];
    if (!snap || !canvas) return;
    try {
      canvas.getContext("2d").putImageData(snap, 0, 0);
    } catch {
      // Ignore restore failures; the page will open blank.
    }
  }, []);

  const renderPageRef = useRef(async () => {});

  const registerPdfCanvas = useCallback((pageNumber, node) => {
    if (node) {
      pdfCanvasRefs.current[pageNumber] = node;
      if (annotationCanvasRefs.current[pageNumber]) {
        renderPageRef.current(pageNumber);
      }
      return;
    }
    const prev = pdfCanvasRefs.current[pageNumber];
    releaseCanvas(prev);
    delete pdfCanvasRefs.current[pageNumber];
  }, []);

  const registerAnnotationCanvas = useCallback(
    (pageNumber, node) => {
      if (node) {
        annotationCanvasRefs.current[pageNumber] = node;
        if (pdfCanvasRefs.current[pageNumber]) {
          renderPageRef.current(pageNumber);
        }
        return;
      }
      const prev = annotationCanvasRefs.current[pageNumber];
      snapshotAnnotation(pageNumber, prev);
      releaseCanvas(prev);
      delete annotationCanvasRefs.current[pageNumber];
    },
    [snapshotAnnotation]
  );

  const setPageWrapperRef = (pageNumber) => (node) => {
    if (node) pageWrapperRefs.current[pageNumber] = node;
    else delete pageWrapperRefs.current[pageNumber];
  };

  const activateSourcePage = useCallback(
    (sourcePage) => {
      setCurrentPage(hasCover ? sourcePage + 1 : sourcePage);
    },
    [hasCover]
  );

  const renderPage = useCallback(async (pageNumber, shouldCancel) => {
    const paint = async () => {
      const pdfDoc = pdfDocRef.current;
      if (!pdfDoc || shouldCancel?.()) return;

      const pdfCanvas = pdfCanvasRefs.current[pageNumber];
      const annotationCanvas = annotationCanvasRefs.current[pageNumber];
      if (!pdfCanvas || !annotationCanvas) return;

      const page = await pdfDoc.getPage(pageNumber);
      if (shouldCancel?.() || pdfCanvasRefs.current[pageNumber] !== pdfCanvas) return;

      const viewport = getCappedViewport(
        page,
        renderScaleRef.current,
        maxCanvasEdgeRef.current
      );
      const width = Math.floor(viewport.width);
      const height = Math.floor(viewport.height);
      if (width < 1 || height < 1) return;

      if (pdfCanvas.width !== width || pdfCanvas.height !== height) {
        pdfCanvas.width = width;
        pdfCanvas.height = height;
        const ctx = pdfCanvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height);
        const task = page.render({
          canvasContext: ctx,
          viewport,
          background: "rgb(255,255,255)",
        });
        const current = renderTasksRef.current[pageNumber];
        if (current) current.task = task;
        await task.promise;
      }

      if (annotationCanvas.width !== width || annotationCanvas.height !== height) {
        annotationCanvas.width = width;
        annotationCanvas.height = height;
        restoreAnnotation(pageNumber, annotationCanvas);
      }
    };

    const prev = renderTasksRef.current[pageNumber]?.done || Promise.resolve();
    const done = prev.then(paint, paint);
    renderTasksRef.current[pageNumber] = {
      ...(renderTasksRef.current[pageNumber] || {}),
      done,
    };
    try {
      await done;
    } catch (error) {
      if (error?.name !== "RenderingCancelledException") {
        // Keep the placeholder visible; a later paint can retry.
      }
    }
  }, [restoreAnnotation]);
  renderPageRef.current = renderPage;

  useEffect(() => {
    if (!isOpen || !filename) return;

    let cancelled = false;
    pdfCanvasRefs.current = {};
    annotationCanvasRefs.current = {};
    pageWrapperRefs.current = {};
    annotationSnapshotsRef.current = {};
    dirtyPagesRef.current = {};
    renderScaleRef.current = getPreferredRenderScale();
    maxCanvasEdgeRef.current = getMaxCanvasEdge();
    setCurrentPage(1);
    setPageMetas([]);
    setAutoFit(true);
    setManualZoom(1);
    setFitZoom(1);
    setStrokeColor(DEFAULT_STROKE_COLOR);
    setStrokeWidth(DEFAULT_STROKE_WIDTH);
    setFingerDraw(!prefersFingerPan());
    setIsErasing(false);
    setIsTexting(false);
    setTextDraft(null);
    setLoading(true);

    const loadPdf = async () => {
      try {
        const bytes = await fetchPdfBytes(filename);
        if (cancelled) return;

        pdfBytesRef.current = bytes;
        const pdfDoc = await pdfjs.getDocument({ data: bytes.slice(0) }).promise;
        if (cancelled) {
          await destroyPdfDoc(pdfDoc);
          return;
        }

        const metas = [];
        for (let pageNumber = 1; pageNumber <= pdfDoc.numPages; pageNumber++) {
          const page = await pdfDoc.getPage(pageNumber);
          const viewport = getCappedViewport(
            page,
            renderScaleRef.current,
            maxCanvasEdgeRef.current
          );
          metas.push({
            pageNumber,
            width: Math.floor(viewport.width),
            height: Math.floor(viewport.height),
          });
        }
        if (cancelled) {
          await destroyPdfDoc(pdfDoc);
          return;
        }

        await destroyPdfDoc(pdfDocRef.current);
        pdfDocRef.current = pdfDoc;
        setPageMetas(metas);
        setLoading(false);
      } catch (error) {
        if (!cancelled) {
          const status = error?.response?.status;
          const message =
            status === 404
              ? loadErrorHint
              : status === 401
                ? "Session expired. Please log in again."
                : error?.response?.data?.message ||
                  error?.message ||
                  "Failed to load PDF.";
          toast.error(message);
          setLoading(false);
        }
      }
    };

    loadPdf();
    return () => {
      cancelled = true;
      Object.values(renderTasksRef.current).forEach((entry) => {
        try {
          entry?.task?.cancel?.();
        } catch {
          // Ignore.
        }
      });
      renderTasksRef.current = {};
      Object.values(pdfCanvasRefs.current).forEach(releaseCanvas);
      Object.values(annotationCanvasRefs.current).forEach(releaseCanvas);
      annotationSnapshotsRef.current = {};
      dirtyPagesRef.current = {};
      const doc = pdfDocRef.current;
      pdfDocRef.current = null;
      pdfBytesRef.current = null;
      destroyPdfDoc(doc);
    };
  }, [isOpen, filename, fetchPdfBytes, loadErrorHint]);

  useEffect(() => {
    if (!isOpen || loading || !pageCount) return undefined;
    let cancelled = false;
    const pages = Array.from(nearbySourcePages);

    const paint = async () => {
      for (const pageNumber of pages) {
        if (cancelled) return;
        await renderPage(pageNumber, () => cancelled);
      }
    };
    paint();
    return () => {
      cancelled = true;
    };
  }, [isOpen, loading, pageCount, nearbySourcePages, renderPage, hasCover]);

  useEffect(() => {
    if (!hasCover) return;
    const root = pagesScrollRef.current;
    if (root) root.scrollTop = 0;
    setCurrentPage(1);
  }, [hasCover]);

  const updateFitZoom = useCallback(() => {
    const container = viewportContainerRef.current;
    if (!container || !maxPageWidth) return;
    const availableWidth = container.clientWidth - 16;
    if (availableWidth <= 0) return;
    const nextZoom = Math.min(getMaxZoom(), Math.max(MIN_ZOOM, availableWidth / maxPageWidth));
    setFitZoom(nextZoom);
  }, [maxPageWidth]);

  useEffect(() => {
    if (!autoFit || !maxPageWidth) return undefined;
    updateFitZoom();

    const container = viewportContainerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(() => updateFitZoom());
    observer.observe(container);
    return () => observer.disconnect();
  }, [autoFit, maxPageWidth, updateFitZoom]);

  useEffect(() => {
    if (!isOpen || loading || !displayPageCount) return undefined;
    const root = pagesScrollRef.current;
    if (!root) return undefined;

    const updateVisiblePage = () => {
      const rootTop = root.getBoundingClientRect().top;
      let bestPage = 1;
      let bestDelta = Infinity;
      Object.entries(pageWrapperRefs.current).forEach(([page, node]) => {
        if (!node) return;
        const delta = Math.abs(node.getBoundingClientRect().top - rootTop);
        if (delta < bestDelta) {
          bestDelta = delta;
          bestPage = Number(page);
        }
      });
      setCurrentPage((prev) => (prev === bestPage ? prev : bestPage));
    };

    root.addEventListener("scroll", updateVisiblePage, { passive: true });
    updateVisiblePage();
    return () => root.removeEventListener("scroll", updateVisiblePage);
  }, [isOpen, loading, displayPageCount, hasCover]);

  useEffect(() => {
    if (!isOpen || loading || !displayPageCount) return undefined;
    const root = pagesScrollRef.current;
    if (!root) return undefined;

    const gesture = {
      pinching: false,
      startDistance: 1,
      startZoom: 1,
      lastZoom: 1,
      lastCenter: null,
    };

    const onTouchStart = (event) => {
      if (event.touches.length < 2) return;
      gesture.pinching = true;
      gesture.startDistance = Math.max(touchDistance(event.touches[0], event.touches[1]), 1);
      gesture.startZoom = displayZoomRef.current;
      gesture.lastZoom = gesture.startZoom;
      gesture.lastCenter = touchCenter(event.touches[0], event.touches[1]);
      event.preventDefault();
    };

    const onTouchMove = (event) => {
      if (!gesture.pinching || event.touches.length < 2) return;
      event.preventDefault();
      const maxZoom = getMaxZoom();
      const nextZoom = Math.min(
        maxZoom,
        Math.max(
          MIN_ZOOM,
          gesture.startZoom * (touchDistance(event.touches[0], event.touches[1]) / gesture.startDistance)
        )
      );
      const center = touchCenter(event.touches[0], event.touches[1]);
      const lastCenter = gesture.lastCenter || center;
      const rect = root.getBoundingClientRect();
      const scale = nextZoom / (gesture.lastZoom || nextZoom);
      const originX = root.scrollLeft + (lastCenter.x - rect.left);
      const originY = root.scrollTop + (lastCenter.y - rect.top);

      gesture.lastZoom = nextZoom;
      gesture.lastCenter = center;
      root.scrollLeft = originX * scale - (lastCenter.x - rect.left) - (center.x - lastCenter.x);
      root.scrollTop = originY * scale - (lastCenter.y - rect.top) - (center.y - lastCenter.y);

      if (pinchRafRef.current) return;
      pinchRafRef.current = window.requestAnimationFrame(() => {
        pinchRafRef.current = 0;
        setAutoFit(false);
        setManualZoom(Number(gesture.lastZoom.toFixed(3)));
      });
    };

    const onTouchEnd = (event) => {
      if (event.touches.length < 2) gesture.pinching = false;
    };

    root.addEventListener("touchstart", onTouchStart, { passive: false });
    root.addEventListener("touchmove", onTouchMove, { passive: false });
    root.addEventListener("touchend", onTouchEnd);
    root.addEventListener("touchcancel", onTouchEnd);
    const preventGestureZoom = (event) => event.preventDefault();
    root.addEventListener("gesturestart", preventGestureZoom);
    root.addEventListener("gesturechange", preventGestureZoom);
    root.addEventListener("gestureend", preventGestureZoom);
    return () => {
      if (pinchRafRef.current) {
        window.cancelAnimationFrame(pinchRafRef.current);
        pinchRafRef.current = 0;
      }
      root.removeEventListener("touchstart", onTouchStart);
      root.removeEventListener("touchmove", onTouchMove);
      root.removeEventListener("touchend", onTouchEnd);
      root.removeEventListener("touchcancel", onTouchEnd);
      root.removeEventListener("gesturestart", preventGestureZoom);
      root.removeEventListener("gesturechange", preventGestureZoom);
      root.removeEventListener("gestureend", preventGestureZoom);
    };
  }, [isOpen, loading, displayPageCount]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onSelectionChange = () => {
      const active = document.activeElement;
      if (active?.tagName === "TEXTAREA" || active?.tagName === "INPUT") return;
      const sel = window.getSelection?.();
      if (!sel || sel.rangeCount === 0) return;
      const anchor = sel.anchorNode;
      if (!anchor) return;
      const onCanvasPage = Object.values(pageWrapperRefs.current).some(
        (node) => node && (node === anchor || node.contains(anchor))
      );
      if (onCanvasPage) sel.removeAllRanges();
    };
    document.addEventListener("selectionchange", onSelectionChange);
    return () => document.removeEventListener("selectionchange", onSelectionChange);
  }, [isOpen]);

  const zoomOut = () => {
    const base = autoFit ? fitZoom : manualZoom;
    setAutoFit(false);
    setManualZoom(Math.max(MIN_ZOOM, Number((base - ZOOM_STEP).toFixed(2))));
  };

  const zoomIn = () => {
    const base = autoFit ? fitZoom : manualZoom;
    setAutoFit(false);
    setManualZoom(Math.min(getMaxZoom(), Number((base + ZOOM_STEP).toFixed(2))));
  };

  const enableAutoFit = () => {
    setAutoFit(true);
    updateFitZoom();
  };

  const zoomLabel = autoFit ? "Auto" : `${Math.round(displayZoom * 100)}%`;
  const textFontSize = fontSizeFromStroke(strokeWidth);

  const commitTextDraft = useCallback((draft = textDraftRef.current) => {
    const next = draft || textDraftRef.current;
    if (!next || next.committed) return;
    if (textDraftRef.current) textDraftRef.current.committed = true;
    next.committed = true;
    textDraftRef.current = null;
    setTextDraft(null);
    const text = String(next.text || "").trim();
    if (!text) return;
    paintTextOnCanvas(annotationCanvasRefs.current[next.sourcePage], {
      x: next.x,
      y: next.y,
      text,
      fontSize: next.fontSize || fontSizeFromStroke(strokeWidthRef.current),
      color: next.color || strokeColorRef.current,
    });
    markAnnotated(next.sourcePage);
  }, [markAnnotated]);

  const cancelTextDraft = useCallback(() => {
    if (textDraftRef.current) textDraftRef.current.committed = true;
    textDraftRef.current = null;
    setTextDraft(null);
  }, []);

  const handlePlaceText = useCallback(
    (sourcePage, point) => {
      commitTextDraft(textDraftRef.current);
      setTextDraft({
        sourcePage,
        x: point.x,
        y: point.y,
        text: "",
        fontSize: fontSizeFromStroke(strokeWidthRef.current),
        color: strokeColorRef.current,
      });
    },
    [commitTextDraft]
  );

  const clearCurrentPage = () => {
    const sourcePage = hasCover ? currentPage - 1 : currentPage;
    if (sourcePage < 1) return;
    if (textDraftRef.current?.sourcePage === sourcePage) {
      setTextDraft(null);
    }
    const canvas = annotationCanvasRefs.current[sourcePage];
    if (canvas) canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    dirtyPagesRef.current[sourcePage] = false;
    delete annotationSnapshotsRef.current[sourcePage];
  };

  const handleSave = async ({ silent = false, allowEmpty = false } = {}) => {
    if (!pdfBytesRef.current) {
      if (allowEmpty) return null;
      if (!silent) toast.error("PDF is still loading.");
      return null;
    }
    commitTextDraft(textDraftRef.current);

    const annotationCanvases = { ...annotationCanvasRefs.current };
    Object.entries(annotationSnapshotsRef.current).forEach(([page, snap]) => {
      const pageNumber = Number(page);
      const live = annotationCanvases[pageNumber];
      if (live && live.width > 0) return;
      if (!snap) return;
      const canvas = document.createElement("canvas");
      canvas.width = snap.width;
      canvas.height = snap.height;
      try {
        canvas.getContext("2d").putImageData(snap, 0, 0);
        annotationCanvases[pageNumber] = canvas;
      } catch {
        // Skip pages we cannot restore.
      }
    });

    const hasAnyDrawing = Object.keys(dirtyPagesRef.current).some(
      (page) => dirtyPagesRef.current[page] && annotationCanvases[Number(page)]
    );
    if (!hasAnyDrawing) {
      if (allowEmpty) return null;
      if (!silent) toast.info("Add at least one annotation before saving.");
      return null;
    }

    const canvasesToMerge = {};
    Object.keys(dirtyPagesRef.current).forEach((page) => {
      if (!dirtyPagesRef.current[page]) return;
      const canvas = annotationCanvases[Number(page)];
      if (canvas && canvas.width > 0) canvasesToMerge[Number(page)] = canvas;
    });

    setSaving(true);
    try {
      const annotatedBytes = await buildAnnotatedPdf(
        pdfBytesRef.current,
        canvasesToMerge
      );
      const blob = new Blob([annotatedBytes], { type: "application/pdf" });
      const file = new File([blob], filename || defaultFileName, {
        type: "application/pdf",
      });
      onSave?.(file);
      if (!embedded && !silent) onClose?.();
      if (!silent) toast.success("Annotations saved. Submit correction to upload.");
      return file;
    } catch (error) {
      toast.error(
        error?.message?.includes("Failed to fetch")
          ? "Could not merge annotations into the PDF. Try clearing and re-drawing on fewer pages."
          : error?.message || "Failed to save annotated PDF."
      );
      throw error;
    } finally {
      setSaving(false);
    }
  };

  if (saveRef) saveRef.current = handleSave;

  const handleClose = () => {
    setTextDraft(null);
    setIsTexting(false);
    Object.values(renderTasksRef.current).forEach((entry) => {
      try {
        entry?.task?.cancel?.();
      } catch {
        // Ignore.
      }
    });
    renderTasksRef.current = {};
    Object.values(pdfCanvasRefs.current).forEach(releaseCanvas);
    Object.values(annotationCanvasRefs.current).forEach(releaseCanvas);
    pdfCanvasRefs.current = {};
    annotationCanvasRefs.current = {};
    pageWrapperRefs.current = {};
    annotationSnapshotsRef.current = {};
    dirtyPagesRef.current = {};
    pdfBytesRef.current = null;
    const doc = pdfDocRef.current;
    pdfDocRef.current = null;
    destroyPdfDoc(doc);
    onClose?.();
  };

  const renderSaveButton = (compact = false) => (
    <Button
      size={compact ? "xs" : "sm"}
      bg={brandPalette.primary}
      color="white"
      _hover={{ bg: brandPalette.accentDark }}
      onClick={handleSave}
      isLoading={saving}
      isDisabled={loading || pageCount === 0}
      flexShrink={0}
    >
      {compact ? "Save" : "Save annotations"}
    </Button>
  );

  const toolDivider = (
    <Box w="1px" h="20px" bg="var(--border-subtle)" flexShrink={0} />
  );

  const renderToolbar = () => (
    <Flex
      w="100%"
      flexShrink={0}
      align="center"
      gap={2}
      px={2}
      py={1.5}
      borderBottomWidth="1px"
      borderColor="var(--border-subtle)"
      bg="var(--surface-muted)"
      overflowX="auto"
      css={{
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": { height: "6px" },
      }}
    >
      <HStack spacing={1.5} flexShrink={0}>
        <Text fontWeight="800" fontSize="xs" whiteSpace="nowrap">
          {currentPage}/{displayPageCount}
        </Text>
        <IconButton
          aria-label="Clear page"
          title="Clear page"
          icon={<MdDeleteOutline size={16} />}
          size="xs"
          variant="outline"
          onClick={clearCurrentPage}
        />
        <Button
          size="xs"
          leftIcon={<MdPanTool size={14} />}
          variant={fingerDraw || isTexting ? "outline" : "solid"}
          bg={fingerDraw || isTexting ? undefined : brandPalette.primary}
          color={fingerDraw || isTexting ? undefined : "white"}
          _hover={fingerDraw || isTexting ? undefined : { bg: brandPalette.accentDark }}
          onClick={() => {
            commitTextDraft();
            setFingerDraw(false);
            setIsErasing(false);
            setIsTexting(false);
          }}
          title="One finger scrolls · pinch to zoom · Apple Pencil always draws"
        >
          Move
        </Button>
        <Button
          size="xs"
          leftIcon={<MdBrush size={14} />}
          variant={fingerDraw && !isErasing && !isTexting ? "solid" : "outline"}
          bg={fingerDraw && !isErasing && !isTexting ? brandPalette.primary : undefined}
          color={fingerDraw && !isErasing && !isTexting ? "white" : undefined}
          _hover={
            fingerDraw && !isErasing && !isTexting
              ? { bg: brandPalette.accentDark }
              : undefined
          }
          onClick={() => {
            commitTextDraft();
            setFingerDraw(true);
            setIsErasing(false);
            setIsTexting(false);
          }}
          title="Finger draws · pinch to zoom · switch to Move to scroll"
        >
          Draw
        </Button>
        <Button
          size="xs"
          leftIcon={<MdTitle size={14} />}
          variant={isTexting ? "solid" : "outline"}
          bg={isTexting ? brandPalette.primary : undefined}
          color={isTexting ? "white" : undefined}
          _hover={isTexting ? { bg: brandPalette.accentDark } : undefined}
          onClick={() => {
            commitTextDraft();
            setFingerDraw(true);
            setIsErasing(false);
            setIsTexting(true);
          }}
          title="Click the PDF to type text · Enter to place · Esc to cancel"
        >
          Text
        </Button>
        <Button
          size="xs"
          leftIcon={<FaEraser size={12} />}
          variant={isErasing ? "solid" : "outline"}
          bg={isErasing ? brandPalette.primary : undefined}
          color={isErasing ? "white" : undefined}
          _hover={isErasing ? { bg: brandPalette.accentDark } : undefined}
          onClick={() => {
            commitTextDraft();
            setFingerDraw(true);
            setIsErasing(true);
            setIsTexting(false);
          }}
          title="Erase annotations · S/M/L/XL change eraser size"
        >
          Erase
        </Button>
      </HStack>

      {toolDivider}

      <HStack spacing={1} flexShrink={0}>
        {ANNOTATION_COLORS.map((color) => {
          const selected = !isErasing && strokeColor === color.value;
          return (
            <Box
              key={color.id}
              as="button"
              type="button"
              aria-label={`${color.label} ${isTexting ? "text" : "pen"}`}
              title={color.label}
              onClick={() => {
                setStrokeColor(color.value);
                setIsErasing(false);
                setFingerDraw(true);
                if (textDraftRef.current) {
                  setTextDraft((prev) =>
                    prev ? { ...prev, color: color.value } : prev
                  );
                }
              }}
              w="18px"
              h="18px"
              borderRadius="full"
              bg={color.value}
              border="2px solid"
              borderColor={
                selected
                  ? brandPalette.primary
                  : color.id === "white"
                    ? "gray.400"
                    : "blackAlpha.300"
              }
              boxShadow={selected ? `0 0 0 2px ${brandPalette.primary}` : "none"}
              flexShrink={0}
            />
          );
        })}
        {STROKE_SIZES.map((size) => {
          const selected = strokeWidth === size.width;
          const sizeKind = isErasing ? "eraser" : isTexting ? "text" : "stroke";
          return (
            <Button
              key={size.id}
              size="xs"
              aria-label={`${size.label} ${sizeKind}`}
              title={
                isTexting
                  ? `${size.label} text size`
                  : isErasing
                    ? `${size.label} eraser`
                    : `${size.label} stroke`
              }
              variant={selected ? "solid" : "outline"}
              bg={selected ? brandPalette.primary : "var(--surface-raised)"}
              color={selected ? "white" : "var(--text-primary)"}
              borderColor={selected ? brandPalette.primary : "var(--border-subtle)"}
              fontWeight="800"
              minW="26px"
              px={1}
              h="22px"
              onClick={() => {
                setStrokeWidth(size.width);
                if (textDraftRef.current) {
                  setTextDraft((prev) =>
                    prev ? { ...prev, fontSize: size.fontSize } : prev
                  );
                }
              }}
              _hover={selected ? { bg: brandPalette.accentDark } : undefined}
            >
              {size.label}
            </Button>
          );
        })}
      </HStack>

      {toolDivider}

      <HStack spacing={1} flexShrink={0} ml="auto">
        <IconButton
          aria-label="Zoom out"
          icon={<MdZoomOut size={16} />}
          size="xs"
          variant="outline"
          onClick={zoomOut}
          isDisabled={displayZoom <= MIN_ZOOM}
        />
        <Text fontWeight="800" fontSize="xs" minW="40px" textAlign="center">
          {zoomLabel}
        </Text>
        <IconButton
          aria-label="Zoom in"
          icon={<MdZoomIn size={16} />}
          size="xs"
          variant="outline"
          onClick={zoomIn}
          isDisabled={displayZoom >= getMaxZoom()}
        />
        <IconButton
          aria-label="Auto-size"
          title="Auto-size"
          icon={<MdFitScreen size={16} />}
          size="xs"
          variant={autoFit ? "solid" : "outline"}
          bg={autoFit ? brandPalette.primary : undefined}
          color={autoFit ? "white" : undefined}
          _hover={autoFit ? { bg: brandPalette.accentDark } : undefined}
          onClick={enableAutoFit}
        />
        {toolbarActions}
        {embedded ? renderSaveButton(true) : null}
      </HStack>
    </Flex>
  );

  const annotatorBody =
    loading ? (
      <Flex
        minH={embedded ? "240px" : "320px"}
        h={embedded ? "100%" : undefined}
        align="center"
        justify="center"
      >
        <Spinner color={brandPalette.primary} size="lg" />
      </Flex>
    ) : pageCount === 0 ? (
      <Text color="var(--text-muted)">No PDF pages to display.</Text>
    ) : (
      <Flex
        direction="column"
        align="stretch"
        gap={0}
        w="100%"
        h={embedded ? "100%" : undefined}
        minH={0}
      >
        {renderToolbar()}

        <Box
          ref={(node) => {
            viewportContainerRef.current = node;
            pagesScrollRef.current = node;
          }}
          w="100%"
          flex={embedded ? "1 1 0" : undefined}
          minH={0}
          overflow="auto"
          maxH={embedded ? undefined : "78vh"}
          bg="var(--surface-muted)"
          style={{
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
            touchAction: "pan-x pan-y",
          }}
        >
          <Flex direction="column" align="center" gap={3} py={2} px={2}>
            {displayPages.map((page) => {
              const pageWidth = page.width ? `${page.width * displayZoom}px` : "100%";
              const feedbackQuestions = page.isCover ? null : pageFeedback?.[page.sourcePage];
              return (
              <Box key={page.isCover ? "results-cover" : page.sourcePage} w="100%">
                <Text
                  fontSize="10px"
                  fontWeight="700"
                  color="var(--text-muted)"
                  mb={1}
                  textAlign="center"
                >
                  Page {page.displayPage}
                </Text>
                <Box
                  ref={setPageWrapperRef(page.displayPage)}
                  data-page={page.displayPage}
                  position="relative"
                  border="1px solid"
                  borderColor="var(--border-subtle)"
                  borderRadius="md"
                  overflow="hidden"
                  mx="auto"
                  bg="var(--surface-muted)"
                  onContextMenu={(event) => event.preventDefault()}
                  style={{
                    width: pageWidth,
                    height: page.height ? `${page.height * displayZoom}px` : "auto",
                    maxWidth: autoFit ? "100%" : undefined,
                    ...IOS_NO_SELECT_STYLE,
                  }}
                >
                  {page.isCover ? (
                    <Box position="absolute" left={0} top={0} w="100%" h="100%">
                      <ExamResultsCover
                        studentName={coverPage.studentName}
                        examName={coverPage.examName}
                        awarded={coverPage.awarded}
                        max={coverPage.max}
                      />
                    </Box>
                  ) : nearbySourcePages.has(page.sourcePage) ? (
                    <>
                      <PdfPageCanvas
                        sourcePage={page.sourcePage}
                        strokeColorRef={strokeColorRef}
                        strokeWidthRef={strokeWidthRef}
                        isErasingRef={isErasingRef}
                        fingerDraw={fingerDraw}
                        isErasing={isErasing}
                        isTexting={isTexting}
                        onActivatePage={activateSourcePage}
                        onPlaceText={handlePlaceText}
                        onAnnotate={markAnnotated}
                        registerPdfCanvas={registerPdfCanvas}
                        registerAnnotationCanvas={registerAnnotationCanvas}
                      />
                      {textDraft?.sourcePage === page.sourcePage ? (
                        <TextDraftInput
                          key={`${textDraft.sourcePage}-${textDraft.x}-${textDraft.y}`}
                          draft={textDraft}
                          color={textDraft.color || strokeColor}
                          fontSize={textDraft.fontSize || textFontSize}
                          displayZoom={displayZoom}
                          canvasWidth={page.width}
                          canvasHeight={page.height}
                          onChange={(text) =>
                            setTextDraft((prev) => (prev ? { ...prev, text } : prev))
                          }
                          onCommit={(text) => {
                            const latest = textDraftRef.current;
                            if (!latest) return;
                            commitTextDraft({ ...latest, text });
                          }}
                          onCancel={cancelTextDraft}
                        />
                      ) : null}
                    </>
                  ) : (
                    <Box
                      position="absolute"
                      left={0}
                      top={0}
                      w="100%"
                      h="100%"
                      bg="white"
                    />
                  )}
                </Box>
                {page.isCover || !nearbySourcePages.has(page.sourcePage) ? null : (
                  <ExamPageFeedback
                    key={`feedback-${page.sourcePage}-${(feedbackQuestions || [])
                      .map((q) => `${q.id}:${q.marks_awarded}:${q.feedback}`)
                      .join("|")}`}
                    pageNumber={page.sourcePage}
                    questions={feedbackQuestions}
                    contextLabel={feedbackContextLabel}
                    width={pageWidth}
                  />
                )}
              </Box>
              );
            })}
          </Flex>
        </Box>
      </Flex>
    );

  if (embedded) {
    if (!isOpen || !filename) return null;

    return (
      <Box
        borderWidth="1px"
        borderColor="var(--border-subtle)"
        borderRadius="xl"
        bg="var(--surface-raised)"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        h="100%"
        minH={0}
      >
        <Box flex="1" minH={0} overflow="hidden" display="flex" flexDirection="column">
          {annotatorBody}
        </Box>
      </Box>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="95vw" dir="ltr">
        <ModalHeader pb={2}>
          <Text fontWeight="900" color="var(--text-primary)">
            {modalTitle}
          </Text>
          <Text fontSize="sm" fontWeight="600" color="var(--text-muted)">
            Draw or type corrections on the submission. Annotations are merged into the PDF on save.
          </Text>
        </ModalHeader>
        <ModalBody>
          {annotatorBody}
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          {renderSaveButton()}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default React.memo(HomeworkPdfAnnotator);
